"use client";

import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useParams, usePathname, useRouter } from "next/navigation";
import useSWR from "swr";
import { toast } from "sonner";
import { apiClient, type AlertDoc } from "@/lib/api-client";
import { telemetry } from "@/lib/telemetry";
import {
  wasRecentlySubscribed,
  consumeRecentlySubscribed,
} from "@/lib/recently-subscribed";
import { useAuth } from "@/contexts/AuthContext";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertItem } from "./sidebar-alert-item";

export const ALERTS_SWR_KEY = "alerts:list";
export const SUBSCRIBED_SWR_KEY = "alerts:subscribed";

export type AlertsVariant = "personal" | "subscribed";

async function alertsFetcher(): Promise<{ alerts: AlertDoc[]; count: number }> {
  return apiClient.listAlerts();
}

async function subscribedAlertsFetcher(): Promise<{
  alerts: AlertDoc[];
  count: number;
}> {
  return apiClient.listSubscribedAlerts();
}

type Props = {
  /**
   * Which list this instance renders:
   *  - "personal"   → alerts the user owns (Pause / Resume / Delete)
   *  - "subscribed" → alerts the user subscribed to (Unsubscribe only)
   * The two variants power two separate top-level sidebar sections so
   * personal and subscribed alerts never share a list.
   */
  variant?: AlertsVariant;
};

export function SidebarAlerts({ variant = "personal" }: Props) {
  const isPersonal = variant === "personal";
  const { setOpenMobile } = useSidebar();
  const { user: firebaseUser } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();

  // Active row detection — on /alerts/[id], params.id matches alert._id.
  const activeAlertId = useMemo(() => {
    if (!pathname?.startsWith("/alerts/")) return null;
    const id = (params as { id?: string })?.id;
    return typeof id === "string" ? id : null;
  }, [pathname, params]);

  // One SWR call, keyed + fetched by variant. The two keys are shared across
  // the app (e.g. the section-visibility check in app-sidebar) so SWR dedupes
  // the network calls automatically.
  const { data, error, isLoading, mutate } = useSWR(
    firebaseUser ? (isPersonal ? ALERTS_SWR_KEY : SUBSCRIBED_SWR_KEY) : null,
    isPersonal ? alertsFetcher : subscribedAlertsFetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 10_000,
    }
  );

  // ── New-row entrance animation bookkeeping ──────────────────────────────
  // We pop+shake a subscribed row the instant it's added (e.g. right after a
  // user subscribes on an alert page → optimistic cache insert). We must NOT
  // animate the whole list on first load, so we diff the current ids against
  // the previously-committed ids:
  //   - prev === null  → no baseline yet (still loading) → animate nothing
  //   - first snapshot WITH data establishes the baseline → animate nothing
  //   - any id absent from the baseline thereafter → that row is "just added"
  // Pure compute in render + ref update in an effect = strict-mode safe (no
  // ref mutation during render).
  const alerts = data?.alerts ?? [];
  const idsKey = alerts.map((a) => a._id).join("|");
  const prevIdsRef = useRef<string[] | null>(null);
  const justAddedIds = useMemo(() => {
    // Only the subscribed list animates; personal rows never do.
    if (isPersonal || !data) return new Set<string>();
    const currentIds = idsKey ? idsKey.split("|") : [];
    const result = new Set<string>();
    // (a) Explicit subscribe marker — covers the FIRST subscription, where this
    //     component mounts fresh with the row already in `data`, so the diff in
    //     (b) can't see it (prev is null / baseline).
    for (const id of currentIds) {
      if (wasRecentlySubscribed(id)) result.add(id);
    }
    // (b) Diff vs the last committed snapshot — covers adding a 2nd+ row while
    //     the list is already mounted (e.g. subscribing from another tab/poll).
    const prev = prevIdsRef.current;
    if (prev !== null) {
      for (const id of currentIds) {
        if (!prev.includes(id)) result.add(id);
      }
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, data, isPersonal]);
  useEffect(() => {
    if (!data) return;
    prevIdsRef.current = idsKey ? idsKey.split("|") : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, data]);

  // Consume the just-added markers (so a row never re-animates) and log
  // telemetry that the entrance animation fired. Subscribed-only.
  useEffect(() => {
    if (isPersonal || justAddedIds.size === 0) return;
    justAddedIds.forEach((id) => {
      consumeRecentlySubscribed(id);
      const a = alerts.find((x) => x._id === id);
      telemetry.emit("alert.subscribe_sidebar_animated", {
        alert_id: id,
        kind: a?.kind ?? "alert",
        variant,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justAddedIds]);

  // Per-row busy state so the spinner only appears on the clicked row.
  const [busyId, setBusyId] = useState<string | null>(null);

  // Confirm-delete dialog state (personal only).
  const [deleteTarget, setDeleteTarget] = useState<AlertDoc | null>(null);

  const handleToggleStatus = useCallback(
    async (alert: AlertDoc) => {
      if (busyId) return;
      // Resume from every recoverable non-active state (paused, test_failed,
      // errored) — only an "active" alert gets paused. A binary paused-check
      // here used to PAUSE a test_failed alert instead of resuming it.
      const shouldResume = alert.status !== "active";
      const nextStatus = shouldResume ? "active" : "paused";

      setBusyId(alert._id);

      await mutate(
        (current) => {
          if (!current) return current;
          return {
            ...current,
            alerts: current.alerts.map((a) =>
              a._id === alert._id ? { ...a, status: nextStatus } : a
            ),
          };
        },
        { revalidate: false }
      );

      try {
        if (shouldResume) {
          await apiClient.resumeAlert(alert._id);
          toast.success("Alert resumed");
        } else {
          await apiClient.pauseAlert(alert._id);
          toast.success("Alert paused");
        }
        mutate();
      } catch (err) {
        mutate();
        toast.error(
          shouldResume ? "Failed to resume alert" : "Failed to pause alert"
        );
      } finally {
        setBusyId(null);
      }
    },
    [busyId, mutate]
  );

  const handleRequestDelete = useCallback((alert: AlertDoc) => {
    setDeleteTarget(alert);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);

    const wasActive = activeAlertId === target._id;

    await mutate(
      (current) => {
        if (!current) return current;
        return {
          ...current,
          alerts: current.alerts.filter((a) => a._id !== target._id),
          count: Math.max(0, current.count - 1),
        };
      },
      { revalidate: false }
    );

    try {
      await apiClient.deleteAlert(target._id);
      toast.success("Alert deleted");
      if (wasActive) {
        router.push("/");
      }
      mutate();
    } catch (err) {
      mutate();
      toast.error("Failed to delete alert");
    }
  }, [deleteTarget, activeAlertId, mutate, router]);

  const handleUnsubscribe = useCallback(
    async (alert: AlertDoc) => {
      if (busyId) return;
      setBusyId(alert._id);

      await mutate(
        (current) => {
          if (!current) return current;
          return {
            ...current,
            alerts: current.alerts.filter((a) => a._id !== alert._id),
            count: Math.max(0, current.count - 1),
          };
        },
        { revalidate: false }
      );

      try {
        await apiClient.unsubscribeAlert(alert._id);
        toast.success("Unsubscribed");
        mutate();
      } catch (err) {
        mutate();
        toast.error("Failed to unsubscribe");
      } finally {
        setBusyId(null);
      }
    },
    [busyId, mutate]
  );

  if (!firebaseUser) {
    return (
      <SidebarGroup>
        <SidebarGroupContent>
          <div className="px-2 text-zinc-500 w-full flex flex-row justify-center items-center text-sm gap-2">
            {isPersonal
              ? "Login to view your alerts!"
              : "Login to view subscriptions"}
          </div>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  if (isLoading && !data) {
    return (
      <SidebarGroup>
        <SidebarGroupContent>
          <div className="flex flex-col">
            {[52, 38, 46].map((w) => (
              <div
                key={w}
                className="rounded-md h-8 flex gap-2 px-2 items-center"
              >
                <div
                  className="h-4 rounded-md flex-1 max-w-[--skeleton-width] bg-sidebar-accent-foreground/10"
                  style={{ ["--skeleton-width" as any]: `${w}%` }}
                />
              </div>
            ))}
          </div>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  if (error) {
    return (
      <SidebarGroup>
        <SidebarGroupContent>
          <div className="px-2 text-zinc-500 w-full flex flex-row justify-center items-center text-sm gap-2">
            Couldn&apos;t load alerts.
          </div>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  if (alerts.length === 0) {
    return (
      <SidebarGroup>
        <SidebarGroupContent>
          <div className="px-2 pb-2 flex flex-col items-stretch gap-2">
            <p className="text-xs text-muted-foreground text-center leading-snug">
              {isPersonal ? "No alerts yet" : "No subscriptions yet"}
            </p>
          </div>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  return (
    <>
      <SidebarGroup className="pt-2">
        <SidebarGroupContent>
          <SidebarMenu>
            <div className="flex flex-col gap-2">
              {alerts.map((alert) => (
                <AlertItem
                  key={alert._id}
                  alert={alert}
                  isActive={alert._id === activeAlertId}
                  isBusy={busyId === alert._id}
                  isOwner={isPersonal}
                  justAdded={!isPersonal && justAddedIds.has(alert._id)}
                  onToggleStatus={handleToggleStatus}
                  onDelete={handleRequestDelete}
                  onUnsubscribe={handleUnsubscribe}
                  setOpenMobile={setOpenMobile}
                />
              ))}
            </div>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this alert?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget
                ? `This stops "${deleteTarget.name}" from running and removes it permanently. This cannot be undone.`
                : "This stops the alert from running and removes it permanently. This cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
